import ChevronDownIcon from "@/assets/icons/chevron-down.svg?react";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import {
  Button,
  ButtonVariant,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import classNames from "classnames";
import { ReactElement, ReactNode, useId, useState } from "react";
import styles from "./Dropdown.module.scss";

export type DropdownButtonProps = {
  content?: ReactElement | string;
  children: (props: { isOpen: boolean; close: () => void }) => ReactNode;
  placement?: Placement;
  icon?: ReactElement;
  variant?: ButtonVariant;
  className?: string;
  withIcon?: boolean;
  dismissOnClick?: boolean;
  /**
   * Nom accessible du déclencheur. À renseigner dès que celui-ci n'a pas de
   * libellé visible (déclencheur purement iconographique) : sans lui, le
   * bouton est annoncé vide par les lecteurs d'écran et n'est atteignable ni
   * par `getByRole("button", { name })` ni par les tests e2e.
   */
  ariaLabel?: string;
};
export function DropdownButton({
  content,
  children,
  placement,
  icon = <ChevronDownIcon />,
  variant = "outline",
  className,
  withIcon,
  dismissOnClick = false,
  ariaLabel,
}: DropdownButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(10),
      flip({ fallbackAxisSideDirection: "end" }),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, {
    enabled: dismissOnClick,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    role,
    dismiss,
  ]);
  const close = () => setIsOpen(false);

  const headingId = useId();

  return (
    <>
      <Button
        ref={refs.setReference}
        {...getReferenceProps()}
        ariaLabel={ariaLabel}
        variant={variant}
        icon={withIcon ? icon : undefined}
        className={classNames(className, isOpen && styles.triggerOpen)}
      >
        {content}
      </Button>
      {isOpen && (
        <FloatingPortal>
          <div
            className="Popover"
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 1000 }}
            aria-labelledby={headingId}
            {...getFloatingProps()}
          >
            {children({ isOpen, close })}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
